const _getURLFromObject = obj => {
    const hash = new URLSearchParams(utils.cleanEmpty(obj)).toString();
    const newUrl = `#${hash}`;
    return newUrl
}

const _getHashFromUrl = () => {
    const hash = window.location.hash?.substring(1);
    return hash
}
const _getObjectFromURL = () => {
    const hash = _getHashFromUrl()
    return Object.fromEntries(new URLSearchParams(hash));
}

const utils = {

    formatDt: (dtOrStr) => {
        if (!dtOrStr){
            return ''
        }
        const date = new Date(dtOrStr);

        const day = date.getDate().toString().padStart(2, '0');
        // const month = date.toLocaleString('default', { month: 'short' });
        const month = date.getMonth()+1
        const year = date.getFullYear();

        const formatted = `${day}. ${month}. ${year}`;
        return formatted
    },

    processPagination: paginationData => {
        if (!paginationData){
            return {}
        }
        const data = {
            defaultCurrent: paginationData.current,
            total: paginationData.count,
            pageSize: paginationData.page_size,
            showTotal: (total) => `${total} items`
        }
        return data
    },

    processBackendOpts: beOptsList => {
        return beOptsList.map(x => ({...x,
            label: x.title,
            value: x.id
        }));
    },

    processBackendOptsById: beOptsDict => {
        return Object.fromEntries(
            Object.values(beOptsDict).map(x => [
                x.id,
                {
                    label: x.title,
                    value: x.id
                }
            ])
        );
    },

    // Builds a flat, depth-first ordered option list for hierarchical tags so
    // that children appear right under their parent. Each option carries a
    // 0-based `level` used for indentation.
    buildTagOptions: (list = []) => {
        const childrenMap = {};
        list.forEach(t => {
            const p = t.parent_id ?? null;
            (childrenMap[p] = childrenMap[p] || []).push(t);
        });
        Object.values(childrenMap).forEach(arr =>
            arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        );
        const out = [];
        const walk = (parentId, level) => {
            (childrenMap[parentId] || []).forEach(t => {
                out.push({ ...t, label: t.title, value: t.id, level });
                walk(t.id, level + 1);
            });
        };
        walk(null, 0);
        return out;
    },

    // /api/tags/tree/ sends `children: []` on a leaf, and antd's Table only
    // checks that the key is THERE, not that it holds anything -- so every leaf
    // renders an expander that opens nothing. Dropping the empty arrays drops
    // the expander with them.
    pruneEmptyTagChildren: (nodes = []) =>
        (nodes || []).map(({ children, ...rest }) =>
            children && children.length
                ? { ...rest, children: utils.pruneEmptyTagChildren(children) }
                : rest
        ),

    // Ids of every node that still has children, at any depth: the row keys a
    // tag tree should start expanded on.
    tagParentKeys: (nodes = []) =>
        (nodes || []).flatMap(n =>
            n.children?.length ? [n.id, ...utils.tagParentKeys(n.children)] : []
        ),

    // Flattens a nested tag tree (nodes with `children`) into a flat array.
    flattenTags: (nodes = []) => {
        const out = [];
        const walk = arr => (arr || []).forEach(n => {
            out.push(n);
            walk(n.children);
        });
        walk(nodes);
        return out;
    },

    // Mirrors Tag.MAX_DEPTH in backend/core/models.py, which enforces it in
    // TagsSerializer.validate_parent. Kept here so the pickers can grey out an
    // illegal parent instead of letting the user find out via a 400.
    TAG_MAX_DEPTH: 3,

    // Of the options buildTagOptions produced, the ones that may PARENT a newly
    // created tag. `level` is 0-based while TAG_MAX_DEPTH is 1-based, so a new
    // child of `opt` lands at depth opt.level + 2 -- mind that off-by-one, it is
    // the difference between hiding a legal parent and letting the user pick one
    // the backend will reject with a 400.
    parentableTagOptions: (opts = []) =>
        opts.filter(opt => (opt.level || 0) + 2 <= utils.TAG_MAX_DEPTH),

    // 1-based depth of a tag (a root tag is at depth 1). Cycle-safe.
    tagDepth: (byId = {}, id) => {
        let depth = 1;
        let node = byId[id];
        const seen = new Set([id]);
        while (node && node.parent_id != null && !seen.has(node.parent_id)) {
            seen.add(node.parent_id);
            depth++;
            node = byId[node.parent_id];
        }
        return depth;
    },

    // How a repeat period is worded, and the single source of truth for it:
    // the "Ponavljanje" dropdown and the dashboard's "this repetition is
    // running out" warnings both read it, so the two cannot drift apart.
    // Values are the lowercase strings the backend stores on IntentWizard.
    REPEAT_PERIOD_OPTIONS: [
        { label: '', value: null },
        { label: 'Vsako leto', value: 'yearly' },
        { label: 'Vsak mesec', value: 'monthly' },
        { label: 'Vsak teden', value: 'weekly' },
        { label: 'Vsak dan', value: 'daily' },
    ],

    repeatPeriodLabel: (value) =>
        utils.REPEAT_PERIOD_OPTIONS.find(o => o.value === value)?.label || '',

    // Columns a user may reorder but never hide: without these a row cannot be
    // identified (type/title) or opened (edit). Mirrors LOCKED_COLUMNS in
    // backend/core/org_config.py, which enforces the same rule on write.
    INTENTS_LOCKED_COLUMNS: ["type", "title", "edit"],

    // Effective key order + hidden set for the transactions table.
    //
    // `declaredKeys` are the columns that SURVIVED their `requires` predicate --
    // a column filtered out there does not exist in this mode at all and must
    // never be ordered, listed or user-enableable.
    //
    // Returns a TOTAL order (every declared key appears exactly once) plus the
    // set of keys to hide.
    resolveColumnLayout: (declaredKeys = [], orgConfig = {}, prefs = {}) => {
        // `undefined` means the user has no opinion -> the org default applies.
        // `[]` means they explicitly want none -> honour it. Collapsing the two
        // would make re-enabling an org-hidden column impossible.
        // The two sources mean different things, and must be merged differently:
        //   - the org default is a LEADING PARTIAL order ("type, title, amount,
        //     tag, then everything else"), so the rest simply follows;
        //   - a user's saved order is a TOTAL order, so a key it has never seen
        //     is a genuinely new column and has to be slotted in place.
        const usingUserOrder = Array.isArray(prefs.order);
        const baseOrder = usingUserOrder
            ? prefs.order
            : orgConfig.intents_column_order ?? [];
        const baseHidden = prefs.hidden ?? orgConfig.intents_hidden_columns ?? [];

        const known = new Set(declaredKeys);
        // Copy: prefs come from redux and are frozen in development.
        const order = baseOrder.filter(k => known.has(k));

        declaredKeys.forEach((key, i) => {
            if (order.includes(key)) return;
            if (!usingUserOrder) {
                order.push(key);
                return;
            }
            // Insert after the nearest declared predecessor that survived --
            // NOT appended, which would place a new column past the fixed-right
            // edit/flag run and unpin them.
            let at = 0;
            for (let j = i - 1; j >= 0; j--) {
                const inx = order.indexOf(declaredKeys[j]);
                if (inx !== -1) {
                    at = inx + 1;
                    break;
                }
            }
            order.splice(at, 0, key);
        });

        const locked = new Set(utils.INTENTS_LOCKED_COLUMNS);
        const hidden = new Set(
            baseHidden.filter(k => known.has(k) && !locked.has(k))
        );

        return { order, hidden };
    },

    // Reorder table columns to match a config-supplied key order (see
    // BaseOrgConfig.intents_column_order). Keys listed in `order` lead, in that
    // order; every other column keeps its declared position behind them. Unknown
    // keys are ignored, so an order may name a column another flag filtered out.
    //
    // antd only positions `fixed` columns correctly while they form an unbroken
    // run at the start (left) or end (right). Reordering can strand one in the
    // middle, which renders as a floating/duplicated cell, so any column outside
    // its run loses `fixed`.
    orderColumns: (columns = [], order = []) => {
        if (!order || !order.length) return columns;

        const keyOf = col => col.key ?? col.dataIndex;
        const rank = new Map(order.map((key, i) => [key, i]));
        const lead = [];
        const rest = [];
        columns.forEach(col => (rank.has(keyOf(col)) ? lead : rest).push(col));
        lead.sort((a, b) => rank.get(keyOf(a)) - rank.get(keyOf(b)));
        const ordered = [...lead, ...rest];

        let firstUnfixed = 0;
        while (firstUnfixed < ordered.length && ordered[firstUnfixed].fixed === 'left') {
            firstUnfixed++;
        }
        let lastUnfixed = ordered.length - 1;
        while (lastUnfixed >= 0 && ordered[lastUnfixed].fixed === 'right') {
            lastUnfixed--;
        }

        return ordered.map((col, i) => {
            if (col.fixed === 'left' && i >= firstUnfixed) return { ...col, fixed: undefined };
            if (col.fixed === 'right' && i <= lastUnfixed) return { ...col, fixed: undefined };
            return col;
        });
    },

    // Ancestor-to-leaf title chain for a tag, e.g. ['Gorivo', 'Gorivo Leon'].
    // Returns [] for an unknown id. Cycle-safe, like tagDepth.
    tagAncestryTitles: (byId = {}, id) => {
        const chain = [];
        let node = byId[id];
        const seen = new Set();
        while (node && !seen.has(node.id ?? id)) {
            seen.add(node.id ?? id);
            if (node.title) chain.unshift(node.title);
            node = node.parent_id != null ? byId[node.parent_id] : null;
        }
        return chain;
    },

    // Set of all descendant ids below `id`.
    tagDescendantIds: (list = [], id) => {
        const childrenMap = {};
        list.forEach(t => {
            const p = t.parent_id ?? null;
            (childrenMap[p] = childrenMap[p] || []).push(t.id);
        });
        const res = new Set();
        const stack = [...(childrenMap[id] || [])];
        while (stack.length) {
            const x = stack.pop();
            if (res.has(x)) continue;
            res.add(x);
            (childrenMap[x] || []).forEach(c => stack.push(c));
        }
        return res;
    },

    // Number of levels in the subtree rooted at `id` (a leaf is 1).
    tagSubtreeHeight: (list = [], id) => {
        const childrenMap = {};
        list.forEach(t => {
            const p = t.parent_id ?? null;
            (childrenMap[p] = childrenMap[p] || []).push(t.id);
        });
        const height = node => {
            const ch = childrenMap[node] || [];
            return ch.length ? 1 + Math.max(...ch.map(height)) : 1;
        };
        return height(id);
    },

    // Sentinel carried INSIDE a multi-value id filter (#companies=none) meaning
    // "rows with NO company/project/tag" -- the N/A bucket in the insights cards.
    // MUST match core.constants.NO_VALUE_FILTER in the backend. Non-numeric on
    // purpose: MasterSearch's /^\d+$/ id coercion and the backend's int() tag
    // expansion must never mistake it for a real id.
    // Diacritic-insensitive fold: "Petról" and "PETROL" both become "petrol".
    // Used by MasterSearch's fuzzy scoring and by tag-rule matching.
    //
    // MUST stay behaviourally identical to normalize_text() in
    // backend/core/utils.py -- a rule saved in the drawer that the server
    // normalizes differently would silently match different transactions. The
    // mirrored case tables in Common/tagRuleMatch.test.js and
    // core/tests/test_tag_rules.py are what enforce that.
    //
    // NFD splits an accented letter into base + combining mark, so dropping the
    // marks (\p{M}) folds c/s/z and the rest of Latin in one step; NFC
    // recomposes afterwards. Known limitation shared with Python on purpose: a
    // single-codepoint letter with no combining mark is NOT folded, so 'đ'
    // stays 'đ'. If that ever changes, change BOTH files and BOTH case tables.
    normalizeText: (value) =>
        String(value ?? '')
            .normalize('NFD')
            .replace(/\p{M}/gu, '')
            .normalize('NFC')
            .toLowerCase()
            .trim(),

    NO_VALUE: 'none',

    isNoValue: v => String(v) === utils.NO_VALUE,

    // Pseudo-option for the three FILTER selects. Carries `title` (TagsSelect's
    // optionRender reads option.data.title) and `level` (its indentation).
    noValueOption: (label = 'N/A') => ({
        value: utils.NO_VALUE, label, title: label, level: 0,
    }),

    // query-string v9 defaults to arrayFormat:'none' -> companies=5&companies=7, and
    // Django's QueryDict.get() keeps only the LAST one, so a multi-select would
    // silently filter by one id. The API speaks comma-joined values (the backend
    // splits on ","), which is also what the URL hash round-trips.
    // NOTE: applied per-caller, not inside api.get -- /api/cashflow/ and the merge
    // endpoints read repeated params via request.GET.getlist() and would break.
    toFilterParams: (obj = {}) => Object.fromEntries(
        Object.entries(obj || {}).map(([k, v]) => [
            k,
            Array.isArray(v)
                ? v.filter(x => x !== null && x !== undefined && x !== '').join(',')
                : v,
        ])
    ),

    cleanEmpty: obj => {
        if (obj === null || obj === undefined){
            return null;
        }
        const filteredObj = Object.fromEntries(
            Object.entries(obj).filter(([key, value]) => value !== null && value !== undefined && value !== "" && (Array.isArray(value) ? value.length > 0 : true))
        );
        return filteredObj;
    },
    formatToThousands: (value) => {
        const isNeg = value < 0;
        if (isNeg) {
            value = Math.abs(value);
        }
        let result = null;
        if (value >= 1_000_000) {
            result = `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
        } else if (value >= 1_000) {
            result = `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
        } else {
            result = `${value}`;
        }

        if (isNeg) {
            return `-${result}`;
        } else {
            return `+${result}`;
        }
    },
    formatToMoney: (value, skipSign=false) => {
        const num = parseFloat(value);
        if (isNaN(num)) return "";
        const sign = skipSign ? '' : num >= 0 ? "+" : "-";
        const absValue = Math.abs(num);
        // Format with 2 decimal places and thousands separators
        const formatted = absValue.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `${sign}${formatted} €`;
    },
    getCurrentYear: () => {
        return new Date().getFullYear().toString();
    },
    getCurrentMonth: () => {
        return (new Date().getMonth() + 1).toString().padStart(2, '0'); // e.g., "06"
    },
    getCurrentYearMonth: function () {
        return `ym-${this.getCurrentYear()}-${this.getCurrentMonth()}`;
    },
    getURLFromObject: obj => {
        return _getURLFromObject(obj)
    },
    setURLFromObject: (obj) => {
        const newUrl = _getURLFromObject(obj)
        console.log('setURLFromObject', newUrl)
        window.history.replaceState(null, '', newUrl);
    },

    getObjectFromURL: () => {
        return _getObjectFromURL()
    },

    getUpdatedURL: (updatesObj={}) => {
        const obj = _getObjectFromURL()
        const newObj = Object.assign({}, obj, updatesObj)
        const newUrl =  _getURLFromObject(newObj)
        return newUrl;
    }

}

export default utils
